import {ChangeDetectorRef, Component, inject, OnInit, signal, TemplateRef, viewChild} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {DataService} from '../../services/data';
import {Router} from '@angular/router';
import {AccessService} from '../../services/access';
import {LocalStorageService} from '../../services/local-storage';
import {Header} from '../header/header';
import {BsModalService, BsModalRef, ModalModule} from 'ngx-bootstrap/modal';

@Component({
    selector: 'app-create',
    imports: [
        FormsModule,
        Header,
        ModalModule
    ],
    templateUrl: './create.html',
    styleUrl: './create.css',
})
export class Create implements OnInit {

    private dataService = inject(DataService);
    private accessService = inject(AccessService);
    private router = inject(Router);
    private localStorageService = inject(LocalStorageService);
    private cdr = inject(ChangeDetectorRef);
    private modalService = inject(BsModalService);

    lastContactsModal = viewChild.required<TemplateRef<any>>('lastContactsModal');
    private modalRef?: BsModalRef;

    room: any = {
        date: this.getDefaultDateString(),
        topic: '',
        agenda: '',
        creator: '',
        participants: [],
    };

    loading = signal<boolean>(false);
    lastContacts = signal<any[]>([]);

    contactPickerAvailable = signal<boolean>('contacts' in navigator && 'ContactsManager' in window);

    showDate = signal(false);
    tokenValid = signal(false);

    ngOnInit() {
        const token = this.router.url.split('/')[2];

        this.dataService.request('Client/checkToken', {
            token,
            domain: this.accessService.client.domain
        }).subscribe((response) => {
            this.tokenValid.set(response.tokenValid);

            if (response.tokenValid) {
                this.accessService.token = token;
            }

            this.room.creator = this.localStorageService.getItem('creator') ?? '';
        });

        this.loadLastContacts();
    }

    private getDefaultDateString(): string {
        const now = new Date();
        now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
        return now.toISOString().slice(0, 16);
    }

    protected setDate(date: string) {
        this.showDate.set(date == 'custom');
        if (date === 'now') {
            this.room.date = this.getDefaultDateString();
        }
    }

    protected addParticipant() {
        this.room.participants.push('');
    }

    protected removeParticipant($index: number) {
        this.room.participants.splice($index, 1);
    }

    protected loadLastContacts() {
        this.lastContacts.set(this.localStorageService.getItem('participants') ?? []);
    }

    protected storeLastContacts() {
        // get last contacts from local storage
        this.lastContacts.set(this.localStorageService.getItem('participants') ?? []);

        // merge participants with contacts
        this.room.participants.forEach((participant: any) => {
            if (!this.lastContacts().includes(participant)) this.lastContacts().push(participant);
        })
        this.localStorageService.setItem('participants', this.lastContacts());
    }

    protected removeLastContact($index: number) {
        this.lastContacts().splice($index, 1);
        this.localStorageService.setItem('participants', this.lastContacts());
    }

    protected openLastContactsModal() {
        this.modalRef = this.modalService.show(this.lastContactsModal());
    }

    protected selectContact(contact: string) {
        if (!this.room.participants.includes(contact)) {
            this.room.participants.push(contact);
        }
    }

    protected closeModal() {
        this.modalRef?.hide();
    }

    protected create() {
        this.loading.set(true);
        this.localStorageService.setItem('creator', this.room.creator);

        this.storeLastContacts();

        this.dataService.request('Room/create', this.room).subscribe((response) => {
            this.router.navigate(['/start', this.accessService.token, response.hash]);
        });
    }

    async getContacts() {
        const props = ['name', 'email', 'tel']; // Fields you want to access
        const opts = { multiple: true };       // Allow selecting multiple contacts

        if ('contacts' in navigator && 'ContactsManager' in window) {
            try {
                // @ts-ignore
                const contacts = await navigator.contacts.select(props, opts);
                // push phone numbers into participants list
                contacts.forEach((contact: any) => {
                    if (contact.tel && contact.tel[0]) {
                        this.room.participants.push(contact.tel[0]);
                    } else if (contact.email && contact.email[0]) {
                        this.room.participants.push(contact.email[0]);
                    }
                });

                // tell angular to update the view
                this.cdr.detectChanges();
            } catch (err) {
                console.error('User cancelled or error occurred:', err);
            }
        } else {
            alert('Contact Picker API is not supported on this browser.');
        }
    }
}
